<template>
  <LoadingSpinner v-if="initalLoading" :initialLoad="true" />
  <component :is="streamComponent" v-else />
</template>

<script>
import { defineAsyncComponent } from 'vue';
import { postIssue } from '@/assets/js/mixins';
import LoadingSpinner from '@/components/LoadingSpinner';

const StreamContainer = defineAsyncComponent(() => import('@/components/StreamContainer'));
const ZoeStream = defineAsyncComponent(() => import('@/components/zoovu/ZoeStream'));

export default {
  metaInfo() {
    return {
      htmlAttrs: {
        lang: this.$t('isoCode'),
      },
      title: this.$store.state.stream.title
        ? `${this.$store.state.initial.stream?.title} | ${this.$store.state.initial.stream?.stream_type.platform} | ${this.$store.state.retailer.name} | Interactive Studios`
        : '',
    };
  },

  components: {
    LoadingSpinner,
    ZoeStream,
    StreamContainer,
  },

  mixins: [postIssue],

  data() {
    return {
      initalLoading: true,
      streamComponent: null,
    };
  },

  computed: {
    loading() {
      return this.$store.state.display.loadingMedia;
    },
  },

  watch: {
    loading() {
      // Fallback Image check once content is loaded
      if (!this.loading) {
        this.diagnostics(
          !this.$store.state.stream.display.video && this.$store.state.stream.display.img,
          'Iframe showing offline images'
        );
      }
    },
  },

  async beforeCreate() {
    const { retailer, stream } = this.$route.params;
    this.$store.commit('display/setLoadingMedia', true);

    try {
      // Disable iframe on IE
      if (this.$browser.name === 'ie') {
        this.$store.commit('display/setStreamError', {
          statusCode: 403,
          message: 'Please update your browser',
          hide: true,
        });
      }

      // Set some diagnostic constants
      this.$store.commit('display/setDiagnosticData', stream);

      // Get retailer info + throw error and hide iframe when it's disabled
      await this.$store.dispatch('getRetailer', retailer);

      if (!this.$store.state.retailer.iframe_enabled) {
        this.$store.commit('display/setStreamError', {
          statusCode: 403,
          message: 'Retailer has disabled iframes',
          hide: true,
        });
      }

      // Set locale based on local storage or retailer settings
      let storedLocale = localStorage.getItem('storedLocale');
      storedLocale = JSON.parse(storedLocale);

      this.$store.commit('i18n/setLocale', {
        country: storedLocale?.code ?? this.$store.state.retailer.country,
        locale: storedLocale?.code_region ?? this.$store.state.retailer.locale,
      });

      if (this.$store.state.i18n.locale === 'ar-AE') document.body.classList.add('theme-rtl');

      localStorage.removeItem('storedLocale');
      this.$i18n.locale = storedLocale?.code_region ?? this.$store.state.retailer.locale;

      // Get stream
      await this.$store.dispatch('getStreamOnly', stream);
      if (this.$store.state.retailer.id !== this.$store.state.stream.retailerId) {
        this.$store.commit('display/setStreamError', {
          statusCode: 403,
          message: 'Retailer and stream mismatch.',
          hide: true,
        });
      }

      // Alternate mid sized design setting
      this.$store.commit('display/setStackedView', this.$store.state.stream.stackedView);
      if (this.$store.state.display.stackedView) document.body.classList.add('stacked-view');

      if (this.$store.state.stream.stream_type.name === 'PDP') {
        await this.$store.dispatch('getStream', {
          os: this.$browser.os,
          streamData: this.$store.state.stream,
        });
      }

      // Set theme based on retailer + stream settings
      if (!Object.keys(this.$store.state.theme).length)
        this.$store.commit('setTheme', {
          stream: this.$store.state.stream,
          retailer: this.$store.state.retailer,
        });

      if (this.$store.state.theme.name) document.body.classList.add(this.$store.state.theme.class);

      if (this.$store.state.theme.bg)
        document.body.style.backgroundColor = this.$store.state.theme.bg;

      // Store initial stream in state for reference
      if (!Object.keys(this.$store.state.initial).length) {
        this.$store.commit('initial/setStream', {
          stream: this.$store.state.stream,
        });
      }

      // ZoovuV2 - short circuits the rest of this function if set up correctly.
      // V2 for Surface PDP only
      if (
        this.$store.state.stream.sku.sku_zoovu_codes[retailer]?.version === 2 &&
        ['Surface', 'M365'].includes(this.$store.state.stream.stream_type.platform) &&
        this.$store.state.stream.stream_type.name === 'PDP'
      ) {
        this.$store.commit('setZoovuV2', true);
        this.streamComponent = 'ZoeStream';
        const index = this.$store.state.stream.sku.media_objects.findIndex(sku => sku.zoovuMedia);

        if (index !== -1) {
          await this.$store.dispatch('getZoovuTags', {
            index,
            mediaId: this.$store.state.stream.sku.media_objects[index].id,
            retailer,
            video: this.$store.state.stream.sku.media_objects[index].video_embed,
          });
        }

        if (!this.$store.state.zoovu.error) {
          await this.$store.dispatch('buckets/getShowcase', { stream: this.$store.state.stream });
          this.$store.commit('display/setActiveTab', 'showcase');
          this.$store.commit(
            'display/setActiveStream',
            this.$store.state.buckets.showcase.carousel[0].id
          );
          this.$store.commit('display/setEmbedType', 'media');
          this.$store.commit('display/setLoadingMedia', false);
          this.initalLoading = false;
          return;
        }
      }

      this.streamComponent = 'StreamContainer';

      // Get bucket pagination limits
      await this.$store.dispatch('buckets/getBucketCapacities');

      // Enable buckets based on stream type
      switch (this.$store.state.stream.stream_type.name) {
        case 'PDP':
          this.$store.state.stream.stream_type.platform === 'Windows'
            ? await this.$store.dispatch(
                'display/setWindowsDisplay',
                this.$store.state.initial.stream
              )
            : this.$store.state.stream.stream_type.platform === 'M365'
            ? await this.$store.dispatch(
                'display/setM365PdpDisplay',
                this.$store.state.initial.stream
              )
            : await this.$store.dispatch('display/setPdpDisplay', this.$store.state.initial.stream);
          break;
        case 'Gaming':
          await this.$store.dispatch('display/setGamingDisplay', this.$store.state.initial.stream);
          break;
        case 'Attach':
          await this.$store.dispatch('display/setAttachDisplay', this.$store.state.initial.stream);
          break;
      }

      // Set active tab
      this.$store.dispatch('display/getActiveTab', this.$store.state.initial.stream);

      // Get bucket ids for copilot, explore and m365
      await this.$store.dispatch('buckets/getBucketIds', {
        retailerId: retailer,
        platform: this.$store.state.stream.stream_type.platform,
      });

      const nonPdpBuckets = [
        'explore',
        'm365',
        'promotions',
        'windows',
        'copilot',
        'tips',
        ...(this.$store.state.stream.stream_type.platform === 'Windows' ? ['devices'] : []),
      ];

      // Get carousels for enabled buckets
      const bucketRequests = [];
      for (const bucket of this.$store.getters['display/getEnabledBuckets']) {
        if (bucket === 'showcase') {
          bucketRequests.push(
            this.$store.dispatch('buckets/getShowcase', {
              stream: this.$store.state.stream,
            })
          );
        } else if (nonPdpBuckets.includes(bucket)) {
          bucketRequests.push(
            this.$store.dispatch('buckets/getBucketSchedule', {
              locale: this.$store.state.i18n.locale,
              type: bucket,
            })
          );
        } else {
          let addStreamToSchedule = true;
          if (
            (bucket === 'gaming' && this.$store.state.initial.stream.stream_type.name === 'PDP') ||
            (bucket === 'devices' &&
              this.$store.state.initial.stream.stream_type.name === 'Gaming') ||
            (bucket === 'gaming' &&
              this.$store.state.initial.stream.stream_type.name === 'Attach') ||
            (bucket === 'products' && this.$store.state.initial.stream.stream_type.name === 'PDP')
          ) {
            addStreamToSchedule = false;
          }

          bucketRequests.push(
            this.$store.dispatch('buckets/getCarousel', {
              retailerID: retailer,
              stream: this.$store.state.stream,
              type: bucket,
              addStreamToSchedule,
            })
          );
        }
      }
      await Promise.all(bucketRequests);

      // Save the active stream id in state for logic checks in components
      if (this.$store.state.buckets[this.$store.state.display.activeTab].carousel.length > 0) {
        this.$store.commit(
          'display/setActiveStream',
          this.$store.state.buckets[this.$store.state.display.activeTab].carousel[0].id
        );
      }

      // Set Embed type
      this.$store.commit(
        'display/setEmbedType',
        this.$store.state.buckets[this.$store.state.display.activeTab].carousel[0].content_type
      );

      if (this.$store.state.initial.stream.stream_type.name === 'PDP') {
        this.$store.commit('display/setLoadingMedia', false);
      }
      this.initalLoading = false;
      this.fetchAdditionalData();

      // Once loaded run some diagnostic checks
      this.diagnosticChecks();
      this.sendTrackingData();
    } catch (e) {
      console.error(e);
      this.$store.commit('display/setStreamError', {
        statusCode: e.response ? 404 : 502,
        message: this.$t('unknownError'),
        subMessage: e.response ? '' : this.$t('reloadToTryAgain'),
        showButton: e.response ? false : true,
        hide: false,
      });
    }
  },

  created() {
    // Request URL via height script
    parent.postMessage({ msg: 'URL Request' }, '*');
    // Handle post message from height script for URL
    window.addEventListener('message', this.urlMessageHandler);
  },

  methods: {
    sendTrackingData() {
      this.$plausible.trackPageview(
        {},
        {
          props: {
            retailer_id: this.$store.state.retailer.id,
            retailer_name: this.$store.state.retailer.name,
            stream_name: this.$store.state.stream.title,
            stream_type: this.$store.state.stream?.stream_type?.name,
            platform: this.$store.state.stream?.stream_type?.platform,
            embedded_url: this.$store.state.display.diagnostics.retailerUrl
              ? this.$store.state.display.diagnostics.retailerUrl
              : this.$store.state.display.diagnostics.retailerDomain,
          },
        }
      );
    },
    diagnosticChecks() {
      // Height script check
      this.diagnostics(
        !this.$store.state.display.diagnostics.retailerUrl,
        'Height script not applied'
      );

      // CTA check
      this.diagnostics(
        this.$store.state.stream.stream_type.name !== 'PDP' && !this.$store.state.stream.pdp_url,
        'No CTA fields filled in on iframe'
      );
    },

    diagnostics(condtion, issue) {
      if (condtion && this.$iframe) {
        this.postIframeIssue(issue);
      }
    },

    async fetchAdditionalData() {
      if (this.$store.state.initial.stream.stream_type.name === 'PDP') return;

      if (this.$store.state.initial.stream.stream_type.name === 'Attach') {
        // Get Attach Media and any additional data
        await this.$store.dispatch('buckets/getBucketMedia', {
          id: this.$store.state.buckets.copilot.carousel[0].id,
          retailer: this.$store.state.retailer,
          os: this.$browser.os,
          bucket: this.$store.state.display.activeTab,
        });
        this.$store.commit('setStream', this.$store.state.buckets.copilot.carousel[0]);
        this.$store.dispatch('display/updateDisplay', {
          state: this.$store.state,
          id: this.$store.state.buckets.copilot.carousel[0].id,
          content_type: this.$store.state.buckets.copilot.carousel[0].content_type,
        });
      } else {
        // Get additional stream data needed, media, timestamps, manifest
        await this.$store.dispatch('getStream', {
          os: this.$browser.os,
          streamData: this.$store.state.stream,
        });
      }
      this.$store.commit('display/setLoadingMedia', false);
    },

    urlMessageHandler(e) {
      if (e.data.msg === 'URL Response' && e.data.url) {
        this.$store.commit('display/setDiagnosticRetailerUrl', e.data.url);
        window.removeEventListener('message', this.urlMessageHandler);
      }
    },
  },
};
</script>
